/** Phase 8 Slice 4 — QualityScoreCard.
 *
 * Visualises a single agent_run's LLM-as-judge score:
 *   - radial gauge for the overall score
 *   - per-dimension horizontal bars
 *   - judge rationale + suggested improvements
 *   - "Re-judge" button (manager-only; quality.write)
 *
 * Mounted by AgentRunInspector underneath the safety verdict. The card
 * renders a compact CTA when no LLM score is present yet (e.g. heuristic
 * fallback or backfill pending).
 */
import { useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner";

const DIMENSION_LABEL = {
  relevance: "Relevance", accuracy: "Accuracy", brand_fit: "Brand fit",
  completeness: "Completeness", safety: "Safety", clarity: "Clarity",
  actionability: "Actionability",
};

function scoreColor(v) {
  if (v == null) return "stroke-muted";
  if (v >= 0.8) return "stroke-emerald-500";
  if (v >= 0.6) return "stroke-amber-500";
  return "stroke-rose-500";
}
function scoreText(v) {
  if (v == null) return "text-muted-foreground";
  if (v >= 0.8) return "text-emerald-600";
  if (v >= 0.6) return "text-amber-600";
  return "text-rose-600";
}
function barBg(v) {
  if (v >= 0.8) return "bg-emerald-500";
  if (v >= 0.6) return "bg-amber-500";
  return "bg-rose-500";
}

function RadialGauge({ value, label }) {
  // value: 0..1
  const v = Math.max(0, Math.min(1, value ?? 0));
  const r = 38;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - v);
  return (
    <div className="flex flex-col items-center" data-testid="quality-radial">
      <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} strokeWidth="8"
                fill="none" className="stroke-muted/40" />
        <circle cx="48" cy="48" r={r} strokeWidth="8" fill="none"
                strokeLinecap="round"
                strokeDasharray={c} strokeDashoffset={offset}
                className={scoreColor(value)} />
      </svg>
      <div className={`-mt-[68px] text-2xl font-semibold ${scoreText(value)}`}
           data-testid="quality-radial-value">
        {value != null ? value.toFixed(2) : "—"}
      </div>
      <div className="mt-10 text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function DimensionBar({ name, value }) {
  const v = Math.max(0, Math.min(1, value ?? 0));
  const pct = Math.round(v * 100);
  return (
    <div className="flex items-center gap-2" data-testid={`quality-dim-${name}`}>
      <div className="text-xs w-28 text-muted-foreground truncate">
        {DIMENSION_LABEL[name] || name}
      </div>
      <div className="h-1.5 flex-1 rounded-full bg-muted/30 overflow-hidden">
        <div className={`h-full ${barBg(v)}`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`text-xs tabular-nums w-10 text-right ${scoreText(v)}`}>
        {v.toFixed(2)}
      </div>
    </div>
  );
}

export default function QualityScoreCard({ runId, score, onRescore }) {
  const t = useT();
  const [busy, setBusy] = useState(false);

  async function handleRejudge() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/quality/agent-runs/${runId}/rejudge`,
                                undefined,
                                { skipPermissionToast: false });
      const fresh = r?.data?.score;
      if (fresh && onRescore) onRescore(fresh);
      toast.success(t("quality.rejudge_ok"));
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) {
        toast.error(t("quality.rejudge_forbidden"));
      } else if (code === 504) {
        toast.error(t("quality.rejudge_timeout"));
      } else {
        toast.error(t("quality.rejudge_failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  if (!score) {
    return (
      <Card data-testid="quality-card-empty">
        <CardHeader className="pb-2"><CardTitle className="text-sm">
          {t("quality.card_title")}</CardTitle></CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p data-testid="quality-empty-msg">{t("quality.empty")}</p>
          <Button size="sm" variant="outline" onClick={handleRejudge}
                  disabled={busy}
                  data-testid="quality-rejudge-btn">
            {busy ? t("quality.rejudging") : t("quality.rejudge")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const dims = score.dimensions || {};
  const improvements = score.suggested_improvements || [];
  return (
    <Card data-testid="quality-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">{t("quality.card_title")}</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-[10px]"
                 data-testid="quality-rubric-badge">
            rubric {score.rubric_version || "v1"}
          </Badge>
          <Badge variant="outline" className="font-mono text-[10px]"
                 data-testid="quality-model-badge">
            {score.model || "—"}
          </Badge>
          <Button size="sm" variant="outline" onClick={handleRejudge}
                  disabled={busy}
                  data-testid="quality-rejudge-btn">
            {busy ? t("quality.rejudging") : t("quality.rejudge")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <RadialGauge value={score.overall_score}
                       label={t("quality.overall")} />
          <div className="mt-2 text-[11px] text-muted-foreground text-center"
               data-testid="quality-weakest">
            {score.weakest_dimension &&
              <>{t("quality.weakest")}:&nbsp;
                <span className="font-medium text-foreground">
                  {DIMENSION_LABEL[score.weakest_dimension]
                   || score.weakest_dimension}
                </span></>}
          </div>
        </div>
        <div className="md:col-span-2 space-y-1.5">
          {Object.entries(dims).map(([k, v]) => (
            <DimensionBar key={k} name={k} value={v} />
          ))}
        </div>
        {score.judge_rationale && (
          <div className="md:col-span-3 border-t pt-3"
               data-testid="quality-rationale">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              {t("quality.rationale")}
            </div>
            <p className="text-xs leading-relaxed">{score.judge_rationale}</p>
          </div>
        )}
        {improvements.length > 0 && (
          <div className="md:col-span-3"
               data-testid="quality-improvements">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              {t("quality.improvements")}
            </div>
            <ul className="text-xs list-disc pl-5 space-y-0.5">
              {improvements.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
